import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import AlertBanner from '@/components/common/AlertBanner';
import InputError from '@/components/Forms/InputError';
import TextInput from '@/components/Forms/TextInput';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import useThemeColors from '@/hooks/useThemeColors';
import StaffLayout from '@/layouts/StaffLayout';
import { User } from '@/types';
import { Link, router, useForm } from '@inertiajs/react';
import clsx from 'clsx';
import { Copy, Eye, EyeOff, Key } from 'lucide-react';
import React, { useState } from 'react';

interface EditProps {
    student: User;
    errors?: Record<string, string>;
    success?: boolean;
    credentials?: {
        email: string;
        password: string;
        url: string;
    };
}

const EditContent: React.FC<EditProps> = ({
    student,
    errors: serverErrors,
    credentials,
}) => {
    const { route } = useRoutes();
    const colors = useThemeColors();
    const [showPassword, setShowPassword] = useState(false);
    const [passwordGenerated, setPasswordGenerated] = useState(false);
    const [copied, setCopied] = useState(false);

    const { data, setData, post, processing, errors } = useForm({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        email: student.email || '',
        password: '',
        new_password: '',
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        post(
            route('staff-students-edit', { student_id: student.id }).toString()
        );
    };

    const handleCopyCredentials = async () => {
        if (!credentials) return;
        const message = `Te paso tus nuevas credenciales:\nURL: ${credentials.url}\nUsuario: ${credentials.email}\nPassword: ${credentials.password}`;
        try {
            await navigator.clipboard.writeText(message);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            setCopied(false);
        }
    };

    const allErrors = { ...serverErrors, ...errors };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-3xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Editar Estudiante
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-neutral-300">
                        Modifica la información del estudiante
                    </p>
                </div>

                <div className="rounded-lg bg-white p-6 shadow dark:bg-neutral-800">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* First Name */}
                        <TextInput
                            label="Nombre"
                            name="first_name"
                            value={data.first_name}
                            onChange={(value) => setData('first_name', value)}
                            error={allErrors.first_name}
                        />

                        {/* Last Name */}
                        <TextInput
                            label="Apellido"
                            name="last_name"
                            value={data.last_name}
                            onChange={(value) => setData('last_name', value)}
                            error={allErrors.last_name}
                        />

                        {/* Email */}
                        <TextInput
                            label="Email"
                            name="email"
                            type="email"
                            value={data.email}
                            onChange={(value) => setData('email', value)}
                            error={allErrors.email}
                        />

                        {/* Password Generation Button */}
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                Contraseña
                            </label>
                            <button
                                type="button"
                                onClick={() => {
                                    setData('new_password', 'generate');
                                    setPasswordGenerated(true);
                                }}
                                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                            >
                                <Key size={16} className="mr-2" />
                                Generar nueva contraseña
                            </button>
                            {passwordGenerated && (
                                <p className="mt-2 text-sm text-green-600">
                                    ✓ Se generará una nueva contraseña al
                                    guardar
                                </p>
                            )}
                        </div>

                        {/* Password */}
                        <div>
                            <label
                                htmlFor="password"
                                className="mb-1 block text-sm font-medium text-gray-500 dark:text-neutral-400"
                            >
                                Nueva Contraseña
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    value={data.password}
                                    onChange={(e) =>
                                        setData('password', e.target.value)
                                    }
                                    className={clsx(
                                        'block w-full rounded-md border bg-white px-3 py-2 pr-10 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:ring-2 focus:outline-none dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500',
                                        allErrors.password
                                            ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                                            : `border-gray-300 dark:border-neutral-600 ${colors.inputFocus}`
                                    )}
                                    placeholder="Dejar en blanco para mantener la actual"
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowPassword(!showPassword)
                                    }
                                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                                >
                                    {showPassword ? (
                                        <EyeOff
                                            size={20}
                                            className="text-gray-400 dark:text-neutral-500"
                                        />
                                    ) : (
                                        <Eye
                                            size={20}
                                            className="text-gray-400 dark:text-neutral-500"
                                        />
                                    )}
                                </button>
                            </div>
                            <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                                Dejar en blanco para mantener la contraseña
                                actual. Mínimo 8 caracteres si se proporciona.
                            </p>
                            <InputError error={allErrors.password} />
                        </div>

                        {allErrors.general && (
                            <AlertBanner variant="error">
                                {allErrors.general}
                            </AlertBanner>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-end space-x-3 border-t pt-4">
                            <Link
                                href={route('staff-students-detail', {
                                    student_id: student.id,
                                }).toString()}
                            >
                                <SecondaryButton type="button">
                                    Cancelar
                                </SecondaryButton>
                            </Link>
                            <PrimaryButton type="submit" disabled={processing}>
                                {processing
                                    ? 'Guardando...'
                                    : 'Guardar Cambios'}
                            </PrimaryButton>
                        </div>
                    </form>
                </div>
            </div>

            {/* Credentials Modal */}
            {credentials && (
                <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
                    <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 dark:bg-neutral-800">
                        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                            ✓ Usuario actualizado
                        </h3>
                        <p className="mb-4 text-sm text-gray-600 dark:text-neutral-300">
                            Nueva contraseña generada. Copia el siguiente
                            mensaje para enviarlo al cliente:
                        </p>

                        {/* Copyable Message */}
                        <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-neutral-700 dark:bg-neutral-700">
                            <pre className="font-mono text-sm whitespace-pre-wrap">
                                {`Te paso tus nuevas credenciales:
URL: ${credentials.url}
Usuario: ${credentials.email}
Password: ${credentials.password}`}
                            </pre>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3">
                            <PrimaryButton
                                onClick={handleCopyCredentials}
                                className="flex-1"
                            >
                                <Copy size={16} className="mr-2" />
                                {copied ? 'Copiado!' : 'Copiar mensaje'}
                            </PrimaryButton>
                            <SecondaryButton
                                onClick={() =>
                                    router.visit(
                                        route('staff-students-detail', {
                                            student_id: student.id,
                                        }).toString()
                                    )
                                }
                            >
                                Cerrar
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
            )}
        </StaffLayout>
    );
};

const Edit: React.FC<EditProps> = (props) => {
    return (
        <ThemeWrapper>
            <EditContent {...props} />
        </ThemeWrapper>
    );
};

export default Edit;
